import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, type AuditEvent, type Diff, type Line, type OrderDetail,
} from '../shared/api';
import {
  BackIcon, CheckIcon, DocIcon, Empty, ErrorNote, PlusIcon, RiskPill,
  Seal, Skeleton, StatePill, formatDate, formatDateTime, relativeTime, titleise, useToast,
} from '../shared/ui';
import { ApprovalPanel } from './ApprovalPanel';

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
  });

  const { data: diff } = useQuery({
    queryKey: ['diff', id],
    queryFn: () => api.get<Diff | null>(`/approvals/${id}/diff`),
    enabled: !!order && order.state === 'IN_APPROVAL',
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order', id] });
    qc.invalidateQueries({ queryKey: ['diff', id] });
    qc.invalidateQueries({ queryKey: ['audit', id] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const act = useMutation({
    mutationFn: (action: string) => api.post<OrderDetail>(`/orders/${id}/${action}`),
    onSuccess: (_, action) => {
      refresh();
      toast(
        action === 'submit' ? 'Sent for approval'
          : action === 'publish' ? 'Published'
          : action === 'cancel' ? 'Cancelled'
          : 'Done',
        'ok',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Action failed', 'bad'),
  });

  if (isLoading) return <Skeleton rows={8} />;
  if (error) return <ErrorNote error={error} />;
  if (!order) return null;

  const sealBroken = order.approval_steps.some((s) => s.state === 'INVALIDATED');
  const canEdit = ['DRAFT', 'IN_APPROVAL', 'APPROVED'].includes(order.state);
  const can = (t: string) => order.available_transitions.includes(t);

  return (
    <>
      <div className="row gap-10" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/orders')}>
          <BackIcon /> Orders
        </button>
      </div>

      <div className="page-head">
        <div>
          <div className="eyebrow">{titleise(order.purpose)}</div>
          <h1 className="page-title mono" style={{ fontSize: 27, letterSpacing: '-.5px' }}>
            {order.voucher_no}
          </h1>
          <div className="row gap-10 row-wrap mt-6">
            <StatePill state={order.state} />
            {order.risk && order.risk.count > 0 && (
              <RiskPill level={order.risk.level} count={order.risk.count} />
            )}
            <span className="faint" style={{ fontSize: 12.5 }}>
              {order.created_by_name} · {relativeTime(order.created_at)}
            </span>
          </div>
        </div>

        <div className="row gap-6 row-wrap">
          {can('submit') && (
            <button
              className="btn btn-primary"
              disabled={act.isPending || order.lines.length === 0}
              onClick={() => act.mutate('submit')}
            >
              Send for approval
            </button>
          )}
          {can('publish') && (
            <button className="btn btn-primary" disabled={act.isPending} onClick={() => act.mutate('publish')}>
              Publish
            </button>
          )}
          {can('cancel') && (
            <button className="btn btn-ghost" disabled={act.isPending} onClick={() => act.mutate('cancel')}>
              Cancel order
            </button>
          )}
        </div>
      </div>

      {order.approval_block_reason && order.state === 'IN_APPROVAL' && (
        <div className="alert alert-warn" style={{ marginBottom: 14 }}>
          {order.approval_block_reason}
        </div>
      )}

      <div className="split">
        <div className="col gap-14">
          <div className="card">
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))' }}>
              <Fact label="Total" value={order.total.formatted} tone={order.txn_type === 'DEBIT' ? 'var(--breach)' : 'var(--verify)'} mono />
              <Fact label="Value date" value={formatDate(order.value_date)} />
              <Fact label="Account" value={order.bank_account_label ?? '—'} />
              <Fact label="Type" value={titleise(order.txn_type)} />
              <Fact label="Lines" value={String(order.line_count)} mono />
            </div>
            {order.note && (
              <>
                <hr className="hr" />
                <div className="faint" style={{ fontSize: 13 }}>{order.note}</div>
              </>
            )}
          </div>

          <LinesCard order={order} canEdit={canEdit} onChanged={refresh} />

          {order.risk_findings.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Risk findings</div>
                <span className="ghost" style={{ fontSize: 11 }}>
                  Raised automatically · never blocking
                </span>
              </div>
              <div className="col gap-10">
                {order.risk_findings.map((f) => (
                  <div key={f.id} className={`finding ${f.risk}`}>
                    <div className="finding-title">
                      <RiskPill level={f.risk} /> {f.title}
                    </div>
                    <div className="finding-detail">{f.detail}</div>
                    <div className="ghost mono" style={{ fontSize: 10.5, marginTop: 7 }}>
                      {f.code} · {f.confidence}% confidence · {f.agent}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DocumentsCard orderId={id} />
          <AuditRibbon orderId={id} />
        </div>

        <div className="col gap-14">
          <div className="card">
            <div className="card-head">
              <div className="card-title">Approval seal</div>
            </div>
            <Seal digest={order.payload_hash} broken={sealBroken} />
            <div className="faint mt-10" style={{ fontSize: 12, lineHeight: 1.6 }}>
              {sealBroken
                ? 'This order changed after it was approved, so every approval already granted has been invalidated. It must be approved again against the new terms.'
                : 'Each approval is bound to this digest. If any figure changes, the digest changes and approvals are invalidated automatically.'}
            </div>
          </div>

          {diff?.has_changes && <DiffCard diff={diff} />}

          <ApprovalPanel order={order} onChanged={refresh} />

          <div className="card">
            <div className="card-head"><div className="card-title">Approval chain</div></div>
            <Ladder order={order} />
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Facts ─────────────────────────────────────────────────────────────── */

function Fact({ label, value, tone, mono }: { label: string; value: string; tone?: string; mono?: boolean }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div
        className={mono ? 'mono' : ''}
        style={{ fontSize: 15, fontWeight: 600, color: tone, marginTop: 5 }}
      >
        {value}
      </div>
    </div>
  );
}

/* ── Approval ladder ───────────────────────────────────────────────────── */

function Ladder({ order }: { order: OrderDetail }) {
  const steps = [...order.approval_steps].sort((a, b) => a.level - b.level);
  if (steps.length === 0) {
    return (
      <div className="faint" style={{ fontSize: 13 }}>
        The chain is built when the order is sent for approval, from the policy that
        matches its amount and purpose.
      </div>
    );
  }

  const currentLevel = steps.find((s) => s.state === 'PENDING' || s.state === 'INVALIDATED')?.level;

  return (
    <div className="ladder">
      {steps.map((s, i) => {
        const cls =
          s.state === 'APPROVED' ? 'done'
            : s.state === 'REJECTED' ? 'reject'
            : s.state === 'INVALIDATED' ? 'void'
            : s.level === currentLevel ? 'active'
            : '';
        return (
          <div key={s.id} className={`rung ${cls}`}>
            <div className="rung-spine">
              <div className="rung-node">
                {s.state === 'APPROVED' ? <CheckIcon /> : s.level}
              </div>
              {i < steps.length - 1 && <div className="rung-line" />}
            </div>
            <div className="rung-body">
              <div className="rung-role">{titleise(s.role_code)}</div>
              <div className="rung-meta">
                {s.state === 'APPROVED' && s.decided_at
                  ? `Approved by ${s.decided_by_name} · ${relativeTime(s.decided_at)}`
                  : s.state === 'REJECTED'
                  ? `Rejected by ${s.decided_by_name}`
                  : s.state === 'INVALIDATED'
                  ? 'Approval invalidated — the order changed'
                  : s.level === currentLevel
                  ? 'Waiting for a decision'
                  : 'Not started'}
              </div>
              {s.comment && (
                <div className="faint" style={{ fontSize: 12, marginTop: 5, fontStyle: 'italic' }}>
                  “{s.comment}”
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Diff ──────────────────────────────────────────────────────────────── */

function DiffCard({ diff }: { diff: Diff }) {
  return (
    <div className="card" style={{ borderColor: 'var(--breach)' }}>
      <div className="card-head">
        <div className="card-title" style={{ color: 'var(--breach)' }}>
          What changed since you approved
        </div>
      </div>
      <div className="faint" style={{ fontSize: 12.5, marginBottom: 12 }}>
        You approved this on {diff.approved_at ? formatDateTime(diff.approved_at) : 'an earlier date'}.
        {' '}
        {diff.change_count} change{diff.change_count === 1 ? '' : 's'} were made afterwards,
        {' '}
        moving the total by <strong className="mono">{diff.total_delta_formatted}</strong>.
      </div>

      <div>
        {diff.changes.map((c, i) => (
          <div className="diff-row" key={i}>
            <span className="diff-key">
              {c.kind === 'field' ? titleise(c.field ?? '') : `Line ${c.line_no}`}
            </span>
            {c.kind === 'field' && (
              <>
                <span className="diff-old">{String(c.before ?? '—')}</span>
                <span className="diff-arrow">→</span>
                <span className="diff-new">{String(c.after ?? '—')}</span>
              </>
            )}
            {c.kind === 'line_added' && <span className="diff-new">Line added</span>}
            {c.kind === 'line_removed' && <span className="diff-old">Line removed</span>}
            {c.kind === 'line_changed' && c.fields && (
              <div className="col gap-6">
                {Object.entries(c.fields).map(([k, v]) => (
                  <div className="row gap-6" key={k}>
                    <span className="ghost" style={{ fontSize: 11.5 }}>{titleise(k)}</span>
                    <span className="diff-old">{String(v.before ?? '—')}</span>
                    <span className="diff-arrow">→</span>
                    <span className="diff-new">{String(v.after ?? '—')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Lines ─────────────────────────────────────────────────────────────── */

function LinesCard({
  order, canEdit, onChanged,
}: { order: OrderDetail; canEdit: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');

  const update = useMutation({
    mutationFn: ({ lineId, amount }: { lineId: string; amount: number }) =>
      api.patch(`/orders/${order.id}/lines/${lineId}`, { amount_minor: amount }),
    onSuccess: () => {
      onChanged();
      setEditing(null);
      toast('Line updated', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not update the line', 'bad'),
  });

  const remove = useMutation({
    mutationFn: (lineId: string) => api.del(`/orders/${order.id}/lines/${lineId}`),
    onSuccess: () => { onChanged(); toast('Line removed', 'ok'); },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not remove the line', 'bad'),
  });

  const startEdit = (line: Line) => {
    setEditing(line.id);
    setValue(line.amount.major);
  };

  const commit = (lineId: string) => {
    const minor = Math.round(Number(value.replace(/[^\d.]/g, '')) * 100);
    if (!Number.isFinite(minor) || minor <= 0) {
      toast('Enter an amount greater than zero', 'bad');
      return;
    }
    update.mutate({ lineId, amount: minor });
  };

  return (
    <div className="card card-flush">
      <div className="card-head" style={{ padding: '18px 18px 0' }}>
        <div className="card-title">Lines</div>
        {canEdit && order.state !== 'DRAFT' && (
          <span className="ghost" style={{ fontSize: 11 }}>
            Editing here invalidates approvals already given
          </span>
        )}
      </div>

      {order.lines.length === 0 ? (
        <Empty title="No lines yet" hint="Add at least one line before sending this order for approval." />
      ) : (
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Beneficiary</th>
                <th>Reference</th>
                <th>Mode</th>
                <th className="right">Amount</th>
                <th>Status</th>
                {canEdit && <th style={{ width: 90 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.id}>
                  <td className="mono ghost">{l.line_no}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{l.beneficiary_name}</div>
                    <div className="ghost mono" style={{ fontSize: 11 }}>{l.beneficiary_code}</div>
                  </td>
                  <td className="mono faint" style={{ fontSize: 12 }}>{l.source_ref ?? '—'}</td>
                  <td><span className="pill pill-draft">{l.payment_mode}</span></td>
                  <td className="num">
                    {editing === l.id ? (
                      <input
                        className="input num"
                        style={{ width: 120 }}
                        value={value}
                        autoFocus
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commit(l.id);
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        onBlur={() => commit(l.id)}
                        aria-label={`Amount for line ${l.line_no}`}
                      />
                    ) : (
                      <span
                        className={order.txn_type === 'DEBIT' ? 'debit' : 'credit'}
                        style={{ cursor: canEdit ? 'text' : 'default' }}
                        onClick={() => canEdit && startEdit(l)}
                        title={canEdit ? 'Click to edit' : undefined}
                      >
                        {l.amount.formatted}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`pill ${l.state === 'PAID' ? 'pill-ok' : l.state === 'FAILED' ? 'pill-bad' : 'pill-draft'}`}>
                      {titleise(l.state)}
                    </span>
                    {l.reference_no && (
                      <div className="ghost mono" style={{ fontSize: 10.5, marginTop: 3 }}>{l.reference_no}</div>
                    )}
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        className="btn btn-bad btn-sm"
                        onClick={() => remove.mutate(l.id)}
                        disabled={remove.isPending || order.lines.length === 1}
                        aria-label={`Remove line ${l.line_no}`}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        className="row between"
        style={{ padding: '13px 18px', borderTop: '1px solid var(--line-soft)' }}
      >
        <span className="label">Total</span>
        <span
          className="mono"
          style={{
            fontSize: 17, fontWeight: 700,
            color: order.txn_type === 'DEBIT' ? 'var(--breach)' : 'var(--verify)',
          }}
        >
          {order.total.formatted}
        </span>
      </div>
    </div>
  );
}

/* ── Documents ─────────────────────────────────────────────────────────── */

interface DocRow {
  id: string; kind: string; filename: string;
  size_bytes: number; sha256: string; created_at: string;
}

function DocumentsCard({ orderId }: { orderId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['docs', orderId],
    queryFn: () => api.get<DocRow[]>(`/documents/orders/${orderId}`),
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.upload(`/documents/orders/${orderId}`, file, '?kind=INVOICE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['docs', orderId] });
      toast('Document attached', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Upload failed', 'bad'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Documents</div>
        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
          <PlusIcon /> Attach
          <input
            type="file"
            className="sr-only"
            accept=".pdf,.png,.jpg,.jpeg,.csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {!data || data.length === 0 ? (
        <div className="faint" style={{ fontSize: 13 }}>
          No documents yet. Attach the invoice or supporting paperwork so approvers can check it.
        </div>
      ) : (
        <div className="col gap-6">
          {data.map((d) => (
            <a
              key={d.id}
              className="row gap-10"
              href={`/api/v1/documents/${d.id}/content`}
              target="_blank"
              rel="noreferrer"
              style={{
                padding: '9px 11px', background: 'var(--ink)', borderRadius: 8,
                textDecoration: 'none', color: 'var(--paper)',
              }}
            >
              <DocIcon />
              <div className="grow">
                <div style={{ fontSize: 13 }}>{d.filename}</div>
                <div className="ghost mono" style={{ fontSize: 10.5 }}>
                  {d.kind} · {(d.size_bytes / 1024).toFixed(0)} KB · {d.sha256.slice(0, 12)}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Audit ribbon ──────────────────────────────────────────────────────── */

function AuditRibbon({ orderId }: { orderId: string }) {
  const { data } = useQuery({
    queryKey: ['audit', orderId],
    queryFn: () => api.get<AuditEvent[]>(`/audit/events?entity_id=${orderId}&limit=40`),
  });

  if (!data || data.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">History</div>
        <span className="ghost" style={{ fontSize: 11 }}>Hash-chained · append only</span>
      </div>
      <div>
        {data.map((e) => (
          <div className="tl-item" key={e.id}>
            <div
              className="tl-dot"
              style={{
                background:
                  e.action.includes('approved') ? 'var(--verify)'
                    : e.action.includes('invalidated') || e.action.includes('rejected') ? 'var(--breach)'
                    : e.action.includes('published') ? 'var(--seal)'
                    : 'var(--faint)',
              }}
            />
            <div className="tl-body">
              <div className="tl-action">{e.summary ?? titleise(e.action)}</div>
              <div className="tl-meta">
                {formatDateTime(e.occurred_at)} · {e.actor_name ?? 'system'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
